import React, { useEffect, useState } from "react";
import { MdViewModule, MdArrowBack } from "react-icons/md";
import { Button, InputGroup, Form } from "react-bootstrap";
import { ClipLoader } from "react-spinners";
import { FaEdit, FaSearch, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import SpecificAPI from "../../../services/SpecificApi";
import localization from "../../../services/localization";
import DeleteModal from "../../Components/DeleteModal";
import ManagementHeader from "../../Components/ManagementHeader";
import CustomSelect from "views/Components/CustomSelect";
import { ModuleCard } from "../../Components/ModuleCard";
import AddEditModuleModal from "./AddEditModuleModal";
import { useModulesStore } from "context/useModulesStore";
import { ALL_TYPES_OPTIONS } from "../../../services/constants";
import { useLanguageStore } from "context/useLanguageStore";

const emptyModule = { name: "", description: "", type: "teaching", icon: "" };
const ModuleManagment = () => {
  const { currentLang } = useLanguageStore();

  const [showAddEditItem, setShowAddEditItem] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState({
    value: "all",
    label: localization.all || "All",
  });
  const [filteredItems, setFilteredItems] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editingItem, setEditingItem] = useState(emptyModule);
  const [itemName, setItemName] = useState("");
  const [errors, setErrors] = useState({});

  const { modules, setModules } = useModulesStore();

  useEffect(() => {
    filterModules();
  }, [searchTerm, selectedType, modules]);

  const validateForm = (form) => {
    const e = {};

    if (!form.name || !form.name.trim()) {
      e.name = localization.name_required || "Name is required";
    }
    if (!form.type || !form.type.trim()) {
      e.type = localization.type_required || "Select a module type";
    }

    return e;
  };

  const handleSubmitItem = async (data) => {
    const validationErrors = validateForm(data);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    try {
      setLoading(true);

      if (editingItem?.id) {
        const res = await SpecificAPI.updateModule({
          ...data,
          _id: editingItem.id,
        });
        setModules((prev) =>
          prev.map((m) => (m.id === editingItem.id ? res.data : m)),
        );
      } else {
        const created = await SpecificAPI.createModule(data);
        setModules((prev) => [...prev, created.data]);
      }
      setShowAddEditItem(false);
      setEditingItem(emptyModule);
      toast.success(localization.module_saved || "Module saved successfully!");
    } catch (err) {
      console.error("Error saving item:", err);
      toast.error(
        err?.response?.data?.error ||
          localization.module_save_failed ||
          "Failed to save module!",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (name, value) => {
    setEditingItem((prev) => {
      if (!prev) return { ...emptyModule, [name]: value };
      return { ...prev, [name]: value };
    });

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setEditingItem((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const filterModules = () => {
    let filtered = modules;
    if (searchTerm) {
      filtered = filtered.filter((cls) =>
        cls.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (selectedType.value !== "all") {
      filtered = filtered.filter(
        (cls) => cls.type?.toLowerCase() === selectedType.value.toLowerCase(),
      );
    }

    setFilteredItems(filtered);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowAddEditItem(true);
    setErrors({});
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setItemName(item.name);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      await SpecificAPI.deleteModule(itemToDelete.id);

      setModules((prev) => prev.filter((ev) => ev.id !== itemToDelete.id));

      toast.success("Module deleted successfully");
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete module. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddEditItem(false);
    setEditingItem(emptyModule);
    setErrors({});
  };

  return (
    <div className="d-flex flex-column mb-5 w-100 main-page p-8">
      <div
        className="d-flex flex-column align-items-start mb-5 w-100"
        style={{ paddingRight: "2rem" }}
      >
        <ManagementHeader
          title={localization.all_modules || "All Modules"}
          subtitle={localization.manage_modules || "Manage modules"}
          // icon={MdViewModule}
          buttonText={localization.add_modules || "Add modules"}
          onButtonClick={() => {
            setEditingItem(emptyModule);
            setShowAddEditItem(true);
            setErrors({});
          }}
          showBack={true}
          backTo="/schools"
          backLabel="Back to organization"
        />

        {/* Filters Row */}
        <div className="d-flex align-items-center gap-3 w-100 mt-4">
          {/* Search Input with Icon */}
          <InputGroup className="search-field flex-grow-1">
            <InputGroup.Text id="search-addon" className="customSearchAddon">
              <FaSearch className="icon-btn" />{" "}
            </InputGroup.Text>
            <Form.Control
              placeholder={
                localization.search_module_placeholder ||
                "Search by module name..."
              }
              aria-label="Search"
              aria-describedby="search-addon"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="customSearchInput"
              style={{}}
            />
          </InputGroup>

          {/* Grade Select */}
          <div style={{ flex: "0 0 250px" }}>
            <CustomSelect
              options={ALL_TYPES_OPTIONS}
              value={selectedType}
              onChange={(opt) => setSelectedType(opt)}
              placeholder={localization.select_type || "Select a type"}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <ClipLoader size={40} color="var(--primary-icon)" />
        </div>
      ) : (
        <div className="row g-4 w-100">
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <div key={item.id} className="col-6 col-md-6 col-lg-4 d-flex">
                <ModuleCard
                  module={item}
                  hasBadge={true}
                  showOnlyIcon={true}
                  footer={
                    <div className="flex gap-2 mt-auto">
                      <Button
                        size="sm"
                        className="action-btn-outline primary flex-1 d-flex align-items-center justify-center gap-2 border transition-all rounded-pill"
                        onClick={() => handleEdit(item)}
                      >
                        <FaEdit className="h-4 w-4" />
                        {localization.edit || "Edit"}
                      </Button>

                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="action-btn-outline danger flex-1 d-flex align-items-center justify-center gap-2 border transition-all rounded-pill"
                        onClick={() => handleDelete(item)}
                      >
                        <FaTrash className="h-4 w-4" />
                        {localization.delete || "Delete"}
                      </Button>
                    </div>
                  }
                />
              </div>
            ))
          ) : (
            <p className="text-center text-muted">
              {localization.no_modules_found || "No modules found."}
            </p>
          )}
        </div>
      )}

      <AddEditModuleModal
        show={showAddEditItem}
        title={
          editingItem?.id
            ? localization.edit_module || "Edit Module"
            : localization.add_module || "Add Module"
        }
        formData={editingItem || emptyModule}
        errors={errors}
        onChange={handleChange}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmitItem(editingItem);
        }}
        onClose={handleCloseModal}
        loading={loading}
        onFormChange={handleFormChange}
      />

      <DeleteModal
        show={showDeleteModal}
        itemName={itemName}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={loading}
        setLoading={setLoading}
      />
    </div>
  );
};

export default ModuleManagment;
